import { API_URL } from "./baseUrl";

const VALID_ROLES = new Set(["admin", "manager", "employee", "client"]);

let memorySession = null; // { userId, role, isEmployee, email, first_name, last_name, hydrated }
let hydratePromise = null;
let tamperGuardStarted = false;

function normalizeRole(role) {
  const value = String(role || "")
    .trim()
    .toLowerCase();
  return VALID_ROLES.has(value) ? value : "";
}

function getStoredAccessToken() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    return user?.accessToken || null;
  } catch {
    return null;
  }
}

export function roleToUserTypeId(role) {
  const normalized = normalizeRole(role) || "client";
  if (normalized === "admin") return 1;
  if (normalized === "manager") return 2;
  if (normalized === "employee") return 3;
  return 4;
}

export function decodeAccessTokenPayload(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length < 2 || !parts[1]) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function readTokenSession() {
  const token = getStoredAccessToken();
  const payload = decodeAccessTokenPayload(token);
  if (!payload?.id) return null;

  const role = normalizeRole(payload.user_type);
  if (!role) return null;

  return {
    userId: String(payload.id).trim(),
    role,
    isEmployee:
      payload.isEmployee === true ||
      payload.isEmployee === "true" ||
      ["admin", "manager", "employee"].includes(role),
    hydrated: false,
  };
}

function readCachedProfile() {
  try {
    return JSON.parse(localStorage.getItem("USER_DATA") || "{}") || {};
  } catch {
    return {};
  }
}

/**
 * Write identity cache keys from the authoritative session.
 * localStorage remains a cache for display/legacy reads — never the source of truth.
 */
export function applySessionToStorage(session) {
  if (!session?.userId || !session?.role) return;
  if (typeof localStorage === "undefined") return;

  localStorage.setItem("user_id", String(session.userId));
  localStorage.setItem("user_role", session.role);

  if (session.email) localStorage.setItem("email", session.email);
  if (session.first_name != null) {
    localStorage.setItem("first_name", session.first_name);
  }
  if (session.last_name != null) {
    localStorage.setItem("last_name", session.last_name);
  }

  const profile = readCachedProfile();
  const nextProfile = {
    ...profile,
    user_type: session.role,
    user_id: session.userId,
  };
  if (session.isEmployee) {
    nextProfile.employee_id = session.userId;
    nextProfile.client_id = null;
  } else {
    nextProfile.client_id = session.userId;
  }
  if (session.email) nextProfile.email = session.email;
  if (session.first_name != null) nextProfile.first_name = session.first_name;
  if (session.last_name != null) nextProfile.last_name = session.last_name;

  // Never keep tokens inside USER_DATA profile cache.
  delete nextProfile.accessToken;
  delete nextProfile.refreshToken;

  localStorage.setItem("USER_DATA", JSON.stringify(nextProfile));
}

function setMemorySession(session) {
  memorySession = session
    ? {
        userId: String(session.userId),
        role: normalizeRole(session.role) || "client",
        isEmployee: Boolean(session.isEmployee),
        email: session.email || null,
        first_name: session.first_name || null,
        last_name: session.last_name || null,
        hydrated: Boolean(session.hydrated),
      }
    : null;
  if (memorySession) applySessionToStorage(memorySession);
  return memorySession;
}

export function clearAuthSessionMemory() {
  memorySession = null;
  hydratePromise = null;
}

/**
 * Ensure cache matches token/memory. Call from getters and before API requests
 * so same-tab localStorage tampering cannot stick.
 */
export function ensureAuthCacheSynced() {
  if (memorySession?.userId && memorySession?.role) {
    applySessionToStorage(memorySession);
    return memorySession;
  }

  const fromToken = readTokenSession();
  if (fromToken) {
    return setMemorySession({ ...fromToken, hydrated: false });
  }

  return null;
}

export function getAuthRole() {
  const session = ensureAuthCacheSynced();
  if (session?.role) return session.role;
  // Unauthenticated / pre-login only.
  return "client";
}

export function getAuthUserId() {
  const session = ensureAuthCacheSynced();
  if (session?.userId) return session.userId;
  return "";
}

export function getAuthUserTypeId() {
  return roleToUserTypeId(getAuthRole());
}

export function getAuthSession() {
  return ensureAuthCacheSynced();
}

/**
 * Seed memory session right after login using API payload (before /me).
 */
export function seedAuthSessionFromLogin(result = {}) {
  const role = normalizeRole(result.user_type) || "client";
  const userId =
    role === "client"
      ? String(result.client_id || result.user_id || "").trim()
      : String(result.employee_id || result.user_id || "").trim();

  if (!userId) return null;

  return setMemorySession({
    userId,
    role,
    isEmployee: role !== "client",
    email: result.email || null,
    first_name: result.first_name || null,
    last_name: result.last_name || null,
    hydrated: true,
  });
}

async function fetchMeWithToken(accessToken) {
  const response = await fetch(`${API_URL}auth/me`, {
    method: "GET",
    credentials: "include",
    headers: {
      "x-access-token": accessToken,
    },
  });

  if (!response.ok) {
    const err = new Error("Failed to hydrate session");
    err.status = response.status;
    throw err;
  }

  const body = await response.json();
  const data = Array.isArray(body?.data) ? body.data[0] : body?.data;
  if (!data || body?.status === 0) {
    const err = new Error(body?.message || "Failed to hydrate session");
    err.status = 401;
    throw err;
  }
  return data;
}

/**
 * Hydrate authoritative role/id from GET /auth/me (JWT verified server-side).
 * Safe to call repeatedly; concurrent callers share one promise.
 */
export async function hydrateAuthSession({ force = false } = {}) {
  if (!force && memorySession?.hydrated) {
    applySessionToStorage(memorySession);
    return memorySession;
  }

  if (hydratePromise && !force) return hydratePromise;

  hydratePromise = (async () => {
    const accessToken = getStoredAccessToken();
    if (!accessToken) {
      clearAuthSessionMemory();
      return null;
    }

    // Prefer token claims immediately so UI doesn't flash wrong role.
    const fromToken = readTokenSession();
    if (fromToken) setMemorySession({ ...fromToken, hydrated: false });

    try {
      const me = await fetchMeWithToken(accessToken);
      const role = normalizeRole(me.user_type) || "client";
      const userId = String(
        me.user_id || me.employee_id || me.client_id || ""
      ).trim();
      if (!userId) {
        clearAuthSessionMemory();
        return null;
      }

      return setMemorySession({
        userId,
        role,
        isEmployee: me.isEmployee === true || role !== "client",
        email: me.email || null,
        first_name: me.first_name || null,
        last_name: me.last_name || null,
        hydrated: true,
      });
    } catch (error) {
      // Keep token-derived session if /me fails transiently but token has claims.
      if (memorySession?.role && memorySession?.userId) {
        return memorySession;
      }
      const fallback = readTokenSession();
      if (fallback) {
        return setMemorySession({ ...fallback, hydrated: false });
      }
      throw error;
    } finally {
      hydratePromise = null;
    }
  })();

  return hydratePromise;
}

export function startAuthTamperGuard() {
  if (tamperGuardStarted || typeof window === "undefined") return;
  tamperGuardStarted = true;

  const resync = () => {
    try {
      ensureAuthCacheSynced();
    } catch {
      /* ignore */
    }
  };

  window.addEventListener("storage", (event) => {
    if (
      event.key === "user_role" ||
      event.key === "user_id" ||
      event.key === "USER_DATA" ||
      event.key === "user"
    ) {
      resync();
    }
  });

  window.addEventListener("focus", resync);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") resync();
  });

  // Catch same-tab DevTools edits between navigations/API calls.
  window.setInterval(resync, 1500);
}
